import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { EventEngineEventsService } from '@amzn/event-engine-events-sdk';
import { CodeBlock, useModal } from '@amzn/event-engine-js-utils';
import download from 'downloadjs';
import React, { FC, useCallback, useMemo } from 'react';
import { i18nStrings } from 'utils/constants';

export interface SshKeyPairModalProps {
    data: EventEngineEventsService.ParticipantGetTeamSshKeyPairResponse;
    visible: boolean;
    onDismiss: () => void;
}

const SshKeyPairModal: FC<SshKeyPairModalProps> = ({
    data,
    onDismiss,
    visible,
}) => {
    const handleDownload = useCallback(() => {
        download(
            data.privateKey,
            `${data.name}.pem`,
            'application/x-x509-ca-cert'
        );
    }, [data]);
    const sections = useMemo(
        () => [
            {
                title: `${i18nStrings.event.headers.sshKey.name}: ${data.name}`,
                content: (
                    <Button onClick={handleDownload} iconName="download">
                        {i18nStrings.event.downloadKeyPair}
                    </Button>
                ),
            },
            {
                title: i18nStrings.event.headers.sshKey.fingerprint,
                content: (
                    <CodeBlock showCopyAction>{data.fingerprint}</CodeBlock>
                ),
            },
            {
                title: i18nStrings.event.headers.sshKey.privateKey,
                content: (
                    <CodeBlock showCopyAction>{data.privateKey}</CodeBlock>
                ),
            },
        ],
        [data, handleDownload]
    );
    const { modalComponent } = useModal({
        isVisible: visible,
        modalHeader: i18nStrings.event.headers.sshKey.title,
        actions: {
            tertiary: {
                text: i18nStrings.close,
                onClick: onDismiss,
            },
        },
        optionalModalProps: {
            size: 'large',
        },
        onDismiss,
        content: (
            <SpaceBetween direction="vertical" size="m">
                {sections.map(({ title, content }) => (
                    <section key={title}>
                        <Box margin={{ bottom: 'xs' }} variant="h3">
                            {title}
                        </Box>
                        {content}
                    </section>
                ))}
            </SpaceBetween>
        ),
    });

    return modalComponent;
};

export default SshKeyPairModal;
